import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "components/common/TooltipButton";
import { LoadMoreButton } from "components/common/LoadMoreButton";
import { LoadingState } from "components/common/LoadingState";
import { useEscapeStack } from "components/hooks/useEscapeStack";
import { useSidebarOverlayStore } from "stores/useSidebarOverlayStore";
import { GovernanceAuditList } from "@/components/main/governance/GovernanceAuditList";
import { useGovernanceAudit } from "@/components/main/governance/useGovernanceAudit";

/// Unmount delay, read off `--duration-medium` so it cannot drift from the
/// transition that plays the panel out.
const exitDelayMs = (): number => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--duration-medium")
    .trim();
  const value = parseFloat(raw);
  if (Number.isNaN(value)) return 0;
  return raw.endsWith("ms") ? value : value * 1000;
};

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  /// Narrows to one space. Omit for the whole org's log.
  spaceId?: string;
  title: string;
  showSpace?: boolean;
  /// Read through the platform admin's door.
  admin?: boolean;
}

export const GovernanceAuditPanel: React.FC<Props> = ({
  open,
  onClose,
  orgId,
  spaceId,
  title,
  showSpace,
  admin,
}) => {
  // Stay mounted through the close animation; `entered` drives the transform.
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const t = setTimeout(() => setMounted(false), exitDelayMs());
    return () => clearTimeout(t);
  }, [open]);

  useEscapeStack(open, onClose);

  const pushOverlay = useSidebarOverlayStore((s) => s.push);
  const popOverlay = useSidebarOverlayStore((s) => s.pop);
  useEffect(() => {
    if (!open) return;
    pushOverlay();
    return () => popOverlay();
  }, [open, pushOverlay, popOverlay]);

  const feed = useGovernanceAudit(
    mounted ? orgId : null,
    spaceId,
    undefined,
    admin,
  );

  if (!mounted) return null;

  const transition = "var(--duration-medium) var(--ease-enter)";

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-50"
        style={{
          opacity: entered ? 1 : 0,
          transition: `opacity ${transition}`,
        }}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-140 bg-bg border-l border-border z-50 flex flex-col shadow-2xl"
        style={{
          transform: entered ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${transition}`,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="governance-audit-title"
      >
        <header className="flex items-center justify-between h-12 px-5">
          <h2 id="governance-audit-title" className="text-fg-bold truncate">
            {title}
          </h2>
          <TooltipButton
            title="Close"
            variant="default"
            size="xs"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </TooltipButton>
        </header>

        <div className="flex-1 overflow-auto px-3 pb-5">
          {feed.available === null ? (
            <LoadingState label="Loading events…" />
          ) : (
            <>
              <GovernanceAuditList
                entries={feed.entries}
                names={feed.names}
                showSpace={showSpace}
              />
              {feed.error && (
                <span className="px-2 text-error">{feed.error}</span>
              )}
              {feed.hasMore && (
                <div className="px-2 pt-2">
                  <LoadMoreButton
                    onClick={feed.loadMore}
                    isLoading={feed.loadingMore}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
};
